import { cn } from "@/lib/utils";
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { type VariantProps, cva } from "class-variance-authority";
import DialogOverlay from "./DialogOverlay";
import { CloseButton } from "@/components/ui/BackButton";

const dialogContentVariants = cva(
  // Enter eases out with the signature curve over 200ms; exit is quicker (150ms)
  // — an overlay should leave faster than it arrives. motion-reduce drops the
  // in/out animation entirely (the web app has no global reduced-motion reset).
  "fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-full translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-2xl border border-border bg-card px-6 py-8 shadow-lg focus-visible:border-none focus-visible:outline-none data-[state=closed]:duration-150 data-[state=open]:duration-200 data-[state=open]:ease-signature data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] motion-reduce:!animate-none md:w-full",
  {
    variants: {
      size: {
        sm: "max-w-[400px]",
        md: "max-w-lg",
        lg: "max-w-[800px]",
        xl: "max-w-[1100px]",
        full: "h-dvh max-h-none max-w-none overflow-visible rounded-none border-none sm:rounded-none",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> &
  VariantProps<typeof dialogContentVariants> & {
    hideCloseButton?: boolean;
  };

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      hideCloseButton,
      size,
      onPointerDownOutside,
      ...props
    },
    ref
  ) => (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onPointerDownOutside={(e) => {
          if (e.detail.originalEvent.pointerType === "touch") {
            e.preventDefault();
          }
          onPointerDownOutside?.(e);
        }}
        className={cn(dialogContentVariants({ size }), className)}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close asChild>
            <CloseButton size="sm" className="absolute right-5 top-5" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

export { dialogContentVariants };
export type { DialogContentProps };
export default DialogContent;
